import { createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'
import { countActiveVenueSuppliers } from 'src/utils/venueSupplierUtils'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'

const initialState = {
  venueSuppliers: [],
  supplierConnectionCount: 0,
  isSomeVenueSupplierActive: null,
  isLoading: false,
}

const venueSuppliersSlice = createSlice({
  name: 'venueSuppliers',
  initialState,
  reducers: {
    setVenueSupplierToState: (state, action) => {
      const suppliers = action.payload?.suppliers ?? action.payload
      const connectionCount =
        action.payload?.connectionCount ?? suppliers?.length ?? 0
      state.venueSuppliers = suppliers
      state.supplierConnectionCount = connectionCount
      state.isSomeVenueSupplierActive = state.venueSuppliers.some(
        venueSupplier =>
          venueSupplier.isActive === true &&
          venueSupplier.isCatalogueAccessible === true,
      )
    },
    editVenueSupplierInState: (state, action) => {
      const index = state.venueSuppliers.findIndex(
        venueSuppliers => venueSuppliers._id === action.payload._id,
      )
      if (index !== -1) {
        state.venueSuppliers[index] = action.payload
        state.supplierConnectionCount = countActiveVenueSuppliers(
          state.venueSuppliers,
        )
        state.isSomeVenueSupplierActive = state.venueSuppliers.some(
          venueSupplier =>
            venueSupplier.isActive === true &&
            venueSupplier.isCatalogueAccessible === true,
        )
      }
    },
    resetVenueSuppliersState: state => {
      state = initialState
      return state
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
  },
})

export const {
  setVenueSupplierToState,
  editVenueSupplierInState,
  resetVenueSuppliersState,
  setIsLoading,
} = venueSuppliersSlice.actions

export const getVenueSuppliers = venueId => async dispatch => {
  try {
    dispatch(setIsLoading(true))
    const _venueSuppliers = await API.get('api', `venue/${venueId}/suppliers`)
    const filteredSuppliers = _venueSuppliers?.filter(
      supplier => supplier && supplier?.nonOMSSupplier !== true,
    )
    dispatch(
      setVenueSupplierToState({
        suppliers: filteredSuppliers,
        connectionCount: countActiveVenueSuppliers(_venueSuppliers),
      }),
    )
    dispatch(setIsLoading(false))
  } catch (e) {
    dispatch(setIsLoading(false))
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const editVenueSupplier = data => async (dispatch, getState) => {
  try {
    const previous = (getState?.()?.venueSuppliers?.venueSuppliers || []).find(
      vs => String(vs?._id) === String(data?._id),
    )
    const wasRestricted = previous?.restricted === true
    const _venueSupplier = await API.post(
      'api',
      `venue/${data.venue}/supplier`,
      {
        body: data,
      },
    )
    dispatch(editVenueSupplierInState(_venueSupplier))
    if (
      !wasRestricted &&
      (_venueSupplier?.restricted === true || data?.restricted === true) &&
      data?.venue
    ) {
      emitGrowthEngineGuidedLaneProgress(
        data.venue,
        GROWTH_ENGINE_GUIDED_LANE_REASONS.SUPPLIER_RESTRICTED,
      )
    }
    return _venueSupplier
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const requestPriceRefresh = (venueId, supplierId) => async dispatch => {
  try {
    await API.post(
      'api2',
      `venue/${venueId}/supplier/${supplierId}/request-price-refresh`,
      { body: {} },
    )
    dispatch(getVenueSuppliers(venueId))
    return true
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const fetchVenueSupplierPriceFiles =
  (venueId, venueSupplierId) => async () => {
    try {
      const session = await Auth.currentAuthenticatedUser()
      const response = await API.get(
        'api2',
        `venue/${venueId}/supplier/${venueSupplierId}/price-files`,
        {
          queryStringParameters: {
            username: session.attributes.sub,
          },
        },
      )
      return {
        success: true,
        title: response?.title,
        currentPriceFileId: response?.currentPriceFileId,
        priceFiles: response?.priceFiles || [],
      }
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      return { success: false, priceFiles: [], message: e?.message }
    }
  }

export const switchVenueSupplierPriceFile =
  ({ venueId, venueSupplierId, priceFileId }) =>
  async dispatch => {
    try {
      const session = await Auth.currentAuthenticatedUser()
      const response = await API.post(
        'api2',
        `venue/${venueId}/supplier/${venueSupplierId}/price-file`,
        {
          body: {
            username: session.attributes.sub,
            priceFileId,
          },
        },
      )
      if (response?.venueSupplier) {
        dispatch(editVenueSupplierInState(response.venueSupplier))
      }
      return response
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const editVenueSuppliers = data => async dispatch => {
  try {
    const venueSuppliers = await API.post(
      'api',
      `venue/${data.venue}/suppliers`,
      {
        body: data,
      },
    )
    dispatch(
      setVenueSupplierToState({
        suppliers: venueSuppliers,
        connectionCount: countActiveVenueSuppliers(venueSuppliers),
      }),
    )
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const resetVenueSuppliers = () => dispatch =>
  dispatch(resetVenueSuppliersState())

export const getVenueByVenueSupplierId = async venueSupplierId => {
  try {
    const venue = await API.get('api', `venue/${venueSupplierId}`)

    return venue
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export default venueSuppliersSlice
